import { useQuery } from '@tanstack/react-query';
import { Globe, Activity, AlertTriangle, Gauge } from 'lucide-react';
import WidgetShell from '@/components/admin/dashboard/WidgetShell';
import StatCard from '@/components/admin/dashboard/StatCard';
import api from '@/lib/api';

/**
 * Cross-tenant Dippy fleet summary (platform admins only — gated by the
 * registry's `superAdmin` flag, so no in-component role guard here).
 * Aggregates the per-tenant rows from GET /admin/platform/agent/fleet into a
 * handful of KPIs and links to the full fleet dashboard.
 */

interface TenantStats {
  active_tasks: number;
  completed_24h: number;
  failed_24h: number;
  escalated_24h: number;
  success_rate_24h: number | null;
  pipeline_orders_24h: number;
  pipeline_success_rate_24h: number | null;
  unread_notifications: number;
  open_escalations: number;
  prospects_active: number;
  suppliers_active: number;
  avg_fulfillment_hours_7d: number | null;
}

interface TenantRow {
  site_id: string;
  site_name: string;
  is_self: boolean;
  error: string | null;
  stats: TenantStats | null;
}

interface FleetResponse {
  tenants: TenantRow[];
}

export default function FleetSummaryWidget() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard', 'platform-fleet'],
    queryFn: () => api.get<FleetResponse>('/admin/platform/agent/fleet'),
    staleTime: 60000,
  });

  const tenants = data?.tenants ?? [];
  const tenantCount = tenants.length;
  const reachable = tenants.filter((t) => !t.error).length;

  const totals = tenants.reduce(
    (acc, t) => {
      if (!t.stats) return acc;
      acc.active_tasks += t.stats.active_tasks;
      acc.open_escalations += t.stats.open_escalations;
      acc.completed_24h += t.stats.completed_24h;
      acc.failed_24h += t.stats.failed_24h;
      acc.escalated_24h += t.stats.escalated_24h;
      return acc;
    },
    { active_tasks: 0, open_escalations: 0, completed_24h: 0, failed_24h: 0, escalated_24h: 0 },
  );

  const attempted = totals.completed_24h + totals.failed_24h + totals.escalated_24h;
  const successRate = attempted > 0 ? (totals.completed_24h * 100) / attempted : null;

  return (
    <WidgetShell
      title="Dippy Fleet"
      icon={<Globe />}
      to="/admin/platform/agent-fleet"
      isLoading={isLoading}
      error={error}
      isEmpty={!!data && tenantCount === 0}
      emptyMessage="No tenants configured in the fleet."
    >
      {data && (
        <div className="grid grid-cols-2 gap-3">
          <StatCard
            title="Tenants"
            value={`${reachable}/${tenantCount}`}
            icon={<Globe />}
            tone="blue"
            subtitle="reachable"
          />
          <StatCard
            title="Active tasks"
            value={totals.active_tasks.toLocaleString()}
            icon={<Activity />}
            tone="teal"
          />
          <StatCard
            title="Open escalations"
            value={totals.open_escalations.toLocaleString()}
            icon={<AlertTriangle />}
            tone={totals.open_escalations > 0 ? 'red' : 'stone'}
          />
          <StatCard
            title="Success 24h"
            value={successRate == null ? '—' : `${successRate.toFixed(1)}%`}
            icon={<Gauge />}
            tone="green"
          />
        </div>
      )}
    </WidgetShell>
  );
}
